import './styles.css';

const DEFAULT_MODEL = import.meta.env.VITE_DEFAULT_MODEL_ID || 'DanVP/MoxhiMT-30-web';
const DEFAULT_MODEL_REVISION = import.meta.env.VITE_DEFAULT_MODEL_REVISION || 'f470e8e125ff9cc0b0f5cc1a8cbbb6741b7370fd';
const MOBILE_SAFE_MODE = isLikelyIOSWebKit();
const MOBILE_DEVICE = isLikelyMobileDevice();
const defaultRuntime = MOBILE_SAFE_MODE
  ? { device: 'wasm', dtype: 'q8', batchSize: 4, maxNewTokens: 160, maxChars: 160, allowLongBatch: false }
  : MOBILE_DEVICE
    ? { device: 'wasm', dtype: 'q8', batchSize: 6, maxNewTokens: 160, maxChars: 160, allowLongBatch: true }
    : { device: 'wasm', dtype: 'q8', batchSize: 6, maxNewTokens: 160, maxChars: 160, allowLongBatch: true };
const DEFAULT_NO_REPEAT_NGRAM_SIZE = 0;
const DEFAULT_REPETITION_PENALTY = 1.2;

const sampleShort = '他抬头看向远处的山门。';
const sampleDialogue = [
  '他说：“你来了。”',
  '她低声说道：“我知道你很累，先休息一下吧。”',
  '他揉了揉眉心，轻轻点头。',
].join('\n');
const sampleLong = [
  '第一章 春',
  '她知道一定是修仙的压力太大，让张羽精神紧绷。',
  '“到了吗？”',
  '“不要紧张，你成绩这么好，一定能过的。”',
  '中间的面试官望着他，淡淡道：“你为什么要报考我们的学校？”',
  '他想了想，随意问道：“你现在每天睡多久？”',
].join('\n');
const sampleStressSentences = [
  '她知道一定是修仙的压力太大，让张羽精神紧绷。',
  '中间的面试官望着他，淡淡道：“你为什么要报考我们的学校？”',
  '他想了想，随意问道：“你现在每天睡多久？”',
  '山门之外云雾翻涌，远处传来低沉的钟声。',
  '少年握紧手中的木牌，心里却比表面平静得多。',
  '长老没有立刻回答，只是抬眼看向殿外的天光。',
];
const sampleStress2k = buildStressSample(2000);
const sampleStress3k = buildStressSample(3000);

document.querySelector('#app').innerHTML = `
  <main class="shell">
    <header class="topbar">
      <div class="brand">
        <p class="eyebrow">Dịch máy Trung → Việt · tiên hiệp / web-novel</p>
        <h1><span class="han" aria-hidden="true">墨希</span>MoxhiMT</h1>
        <p class="tagline">
          Mô hình 30M chạy <strong>ngay trong trình duyệt</strong> của bạn —
          văn bản không được gửi lên bất kỳ máy chủ nào.
        </p>
      </div>
      <div class="seal">
        <span class="seal-stamp" aria-hidden="true">譯</span>
        <div class="status-strip" aria-label="Trạng thái thiết bị">
          <span id="gpuBadge" title="Tăng tốc GPU qua WebGPU">WebGPU: đang kiểm tra</span>
          <span id="isoBadge" title="Cross-origin isolation cho WASM đa luồng">Cách ly: đang kiểm tra</span>
        </div>
      </div>
    </header>

    <section class="mainpane">
      <div class="inputbar">
        <span class="samples-label">Mẫu thử:</span>
        <button data-sample="short" type="button">1 câu</button>
        <button data-sample="dialogue" type="button">Hội thoại</button>
        <button data-sample="long" type="button">Đoạn truyện</button>
        <button data-sample="stress2k" type="button">2k</button>
        <button data-sample="stress3k" type="button">3k</button>
      </div>

      <div class="panes">
        <label class="pane">
          <span class="pane-title">Tiếng Trung (nguồn)</span>
          <textarea id="sourceText" spellcheck="false" placeholder="Dán văn bản tiếng Trung vào đây…">${sampleDialogue}</textarea>
        </label>
        <div class="pane">
          <span class="pane-title">Tiếng Việt (kết quả)</span>
          <pre id="output" class="output" aria-live="polite" data-empty="Bản dịch sẽ hiện ở đây."></pre>
        </div>
      </div>

      <div class="actionbar">
        <button id="translateBtn" type="button" class="primary">Dịch</button>
        <button id="copyBtn" type="button">Sao chép</button>
        <button id="clearBtn" type="button">Xoá</button>
        <div class="progress-wrap">
          <span id="state" class="state">Sẵn sàng</span>
          <progress id="progress" max="1" value="0"></progress>
        </div>
      </div>

      <p id="firstLoadHint" class="firstload-hint" hidden>
        Lần đầu sẽ tải mô hình ~60&nbsp;MB về trình duyệt (mất ~10–60 giây tuỳ mạng).
        Các lần sau mô hình được lưu trong bộ nhớ trình duyệt nên dịch gần như tức thì.
      </p>

      <details class="advanced">
        <summary>Nâng cao (tuỳ chọn runtime &amp; đo hiệu năng)</summary>
        <div class="advanced-body">
          <div class="adv-grid">
            <label>
              Kho mô hình
              <input id="modelId" value="${DEFAULT_MODEL}" spellcheck="false" />
            </label>
            <label>
              Thiết bị (device)
              <select id="device">
                <option value="auto"${selected(defaultRuntime.device, 'auto')}>auto</option>
                <option value="webgpu"${selected(defaultRuntime.device, 'webgpu')}>webgpu</option>
                <option value="wasm"${selected(defaultRuntime.device, 'wasm')}>wasm</option>
              </select>
            </label>
            <label>
              Độ chính xác (dtype)
              <select id="dtype">
                <option value="auto"${selected(defaultRuntime.dtype, 'auto')}>auto</option>
                <option value="fp16"${selected(defaultRuntime.dtype, 'fp16')}>fp16</option>
                <option value="q8"${selected(defaultRuntime.dtype, 'q8')}>q8</option>
                <option value="fp32"${selected(defaultRuntime.dtype, 'fp32')}>fp32</option>
              </select>
            </label>
            <label>
              Max batch
              <input id="batchSize" type="number" min="1" max="8" value="${defaultRuntime.batchSize}" />
            </label>
            <label class="checkline">
              Long batch packing
              <input id="allowLongBatch" type="checkbox"${checked(defaultRuntime.allowLongBatch)} />
            </label>
            <label>
              Beam
              <input id="numBeams" type="number" min="1" max="4" value="1" />
            </label>
            <label>
              Max new tokens
              <input id="maxNewTokens" type="number" min="32" max="512" value="${defaultRuntime.maxNewTokens}" />
            </label>
            <label>
              Chunk chars
              <input id="maxChars" type="number" min="80" max="512" value="${defaultRuntime.maxChars}" />
            </label>
            <label>
              No repeat ngram
              <input id="noRepeatNgramSize" type="number" min="0" max="8" value="${DEFAULT_NO_REPEAT_NGRAM_SIZE}" />
            </label>
            <label>
              Repetition penalty
              <input id="repetitionPenalty" type="number" min="1" max="2" step="0.05" value="${DEFAULT_REPETITION_PENALTY}" />
            </label>
          </div>

          <div class="adv-actions">
            <button id="loadBtn" type="button">Tải mô hình</button>
            <button id="cancelBtn" type="button">Huỷ</button>
            <button id="benchBtn" type="button">Đo hiện tại</button>
            <button id="matrixBtn" type="button">Đo ma trận</button>
            <button id="persistBtn" type="button">Giữ cache</button>
            <button id="copyMetricsBtn" type="button">Copy số liệu</button>
            <button id="clearMetricsBtn" type="button">Xoá số liệu</button>
          </div>

          <div class="metrics-grid">
            <div class="meter"><span>Tải mô hình</span><strong id="loadMetric">-</strong></div>
            <div class="meter"><span>Thời gian dịch</span><strong id="translateMetric">-</strong></div>
            <div class="meter"><span>Tốc độ</span><strong id="throughputMetric">-</strong></div>
            <div class="meter"><span>Lưu lâu dài</span><strong id="persistMetric">đang kiểm tra</strong></div>
            <div class="meter"><span>Bộ nhớ site</span><strong id="storageMetric">-</strong></div>
          </div>

          <table class="bench-table">
            <thead>
              <tr>
                <th>Runtime</th>
                <th>Tải</th>
                <th>Dịch</th>
                <th>Ký tự/s</th>
              </tr>
            </thead>
            <tbody id="benchRows"></tbody>
          </table>

          <pre id="log" class="log" aria-label="Nhật ký runtime"></pre>
        </div>
      </details>
    </section>

    <footer class="foot">
      Chạy hoàn toàn phía client bằng Transformers.js + ONNX Runtime Web ·
      mô hình từ Hugging Face Hub · mã nguồn demo tĩnh.
    </footer>
  </main>
`;

const worker = new Worker(new URL('./worker/translator.worker.js', import.meta.url), { type: 'module' });
let seq = 0;
const pending = new Map();
let runtimeTouched = false;
let modelEverLoaded = false;
let busy = false;
let selectedSampleKey = 'dialogue';
const benchHistory = [];
let storageState = {
  supported: false,
  persisted: false,
  usage: null,
  quota: null,
  requested: false,
  error: null,
};

const $ = (id) => document.getElementById(id);
const fmtMs = (value) => value == null ? '-' : `${Math.round(value)} ms`;
const fmtRate = (value) => value ? `${value.toFixed(1)} ký tự/s` : '-';
const fmtBytes = (value) => {
  if (!Number.isFinite(value)) return '-';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = Math.max(0, value);
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size >= 10 || unit === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unit]}`;
};

function log(line) {
  const target = $('log');
  const time = new Date().toLocaleTimeString();
  target.textContent = `[${time}] ${line}\n${target.textContent}`.slice(0, 5000);
}

function setState(text) {
  $('state').textContent = text;
}

function setBusy(value) {
  busy = value;
  $('translateBtn').disabled = value;
  $('translateBtn').textContent = value ? 'Đang dịch…' : 'Dịch';
  $('loadBtn').disabled = value;
}

function config() {
  const modelId = $('modelId').value.trim() || DEFAULT_MODEL;
  return {
    modelId,
    revision: modelRevisionFor(modelId),
    device: $('device').value,
    dtype: $('dtype').value,
    mobileSafeMode: MOBILE_SAFE_MODE,
  };
}

function modelRevisionFor(modelId) {
  return modelId === DEFAULT_MODEL ? DEFAULT_MODEL_REVISION : 'main';
}

function options() {
  return {
    batchSize: Number($('batchSize').value || 1),
    numBeams: Number($('numBeams').value || 1),
    maxNewTokens: Number($('maxNewTokens').value || 192),
    maxChars: Number($('maxChars').value || 220),
    noRepeatNgramSize: Number($('noRepeatNgramSize').value || DEFAULT_NO_REPEAT_NGRAM_SIZE),
    repetitionPenalty: Number($('repetitionPenalty').value || DEFAULT_REPETITION_PENALTY),
    allowLongBatch: $('allowLongBatch').checked,
  };
}

function callWorker(action, payload = {}) {
  const id = ++seq;
  const promise = new Promise((resolve, reject) => {
    pending.set(id, { action, resolve, reject });
  });
  worker.postMessage({ id, action, ...payload });
  return promise;
}

function updateMetrics(metrics) {
  $('loadMetric').textContent = fmtMs(metrics.loadMs);
  $('translateMetric').textContent = fmtMs(metrics.translateMs);
  $('throughputMetric').textContent = fmtRate(metrics.charsPerSec);
}

function updateStorageMeters() {
  const persistMetric = $('persistMetric');
  const storageMetric = $('storageMetric');
  if (!storageState.supported) {
    persistMetric.textContent = storageState.error ? 'lỗi' : 'không hỗ trợ';
    persistMetric.title = storageState.error || 'Trình duyệt không có StorageManager.';
    storageMetric.textContent = '-';
    storageMetric.title = '';
    return;
  }

  persistMetric.textContent = storageState.persisted ? 'bật' : 'chưa bật';
  persistMetric.title = storageState.persisted
    ? 'Browser đã cấp persistent storage cho cache model.'
    : 'Browser có thể xoá cache khi thiếu dung lượng. Bấm Giữ cache để thử xin lại.';

  if (Number.isFinite(storageState.usage) && Number.isFinite(storageState.quota)) {
    storageMetric.textContent = `${fmtBytes(storageState.usage)} / ${fmtBytes(storageState.quota)}`;
    storageMetric.title = 'Dung lượng site đang dùng / quota trình duyệt cấp.';
  } else {
    storageMetric.textContent = 'không rõ';
    storageMetric.title = 'Trình duyệt không trả về estimate dung lượng.';
  }
}

async function refreshStoragePersistence({ request = false, userInitiated = false } = {}) {
  storageState = {
    ...storageState,
    supported: Boolean(navigator.storage),
    requested: storageState.requested || request,
    error: null,
  };

  if (!navigator.storage) {
    updateStorageMeters();
    if (userInitiated) setState('Trình duyệt không hỗ trợ kiểm tra lưu lâu dài');
    return storageState;
  }

  try {
    let persisted = typeof navigator.storage.persisted === 'function'
      ? await navigator.storage.persisted()
      : false;

    if (!persisted && request && typeof navigator.storage.persist === 'function') {
      persisted = await navigator.storage.persist();
    }

    const estimate = typeof navigator.storage.estimate === 'function'
      ? await navigator.storage.estimate()
      : {};

    storageState = {
      supported: true,
      persisted: Boolean(persisted),
      usage: estimate.usage ?? null,
      quota: estimate.quota ?? null,
      requested: storageState.requested || request,
      error: null,
    };
    updateStorageMeters();

    if (userInitiated) {
      setState(storageState.persisted ? 'Đã bật lưu cache lâu dài' : 'Trình duyệt chưa cấp lưu lâu dài');
    }
    return storageState;
  } catch (error) {
    storageState = {
      ...storageState,
      supported: true,
      persisted: false,
      error: error.message,
    };
    updateStorageMeters();
    if (userInitiated) setState('Không kiểm tra được lưu cache lâu dài');
    return storageState;
  }
}

function recordBench(metrics) {
  const profile = metrics.profile || {};
  benchHistory.unshift({
    at: new Date().toISOString(),
    sample: selectedSampleKey,
    sourceChars: metrics.sourceChars,
    targetChars: metrics.targetChars,
    chunks: metrics.chunks,
    runtime: `${metrics.resolvedDevice || metrics.device}/${metrics.resolvedDtype || metrics.dtype}`,
    loadMs: metrics.loadMs,
    translateMs: metrics.translateMs,
    totalMs: metrics.totalMs,
    charsPerSec: metrics.charsPerSec,
    maxBatchSize: metrics.maxBatchSize,
    maxChars: metrics.maxChars,
    maxNewTokens: metrics.maxNewTokens,
    noRepeatNgramSize: metrics.noRepeatNgramSize,
    repetitionPenalty: metrics.repetitionPenalty,
    allowLongBatch: metrics.allowLongBatch,
    mobileSafeMode: metrics.mobileSafeMode,
    wasmVariant: metrics.wasmVariant,
    wasmNumThreads: metrics.wasmNumThreads,
    disposedObjects: metrics.disposedObjects,
    profile: {
      batches: profile.batches,
      batchSizeCounts: profile.batchSizeCounts,
      eosRows: profile.eosRows,
      noEosRows: profile.noEosRows,
      tokenizeMs: profile.tokenizeMs,
      generateMs: profile.generateMs,
      decodeMs: profile.decodeMs,
      disposeMs: profile.disposeMs,
      trimmedOutputTokenSlots: profile.trimmedOutputTokenSlots,
      decodedChars: profile.decodedChars,
    },
  });
}

function appendBench(metrics) {
  const tr = document.createElement('tr');
  const safe = metrics.mobileSafeMode ? ' safe' : '';
  const packed = metrics.allowLongBatch ? ' packed' : '';
  const runtime = `${metrics.resolvedDevice || metrics.device}/${metrics.resolvedDtype || metrics.dtype}${safe}${packed}`;
  const cells = [runtime, fmtMs(metrics.loadMs), fmtMs(metrics.translateMs), fmtRate(metrics.charsPerSec)];
  for (const value of cells) {
    const td = document.createElement('td');
    td.textContent = value;
    tr.appendChild(td);
  }
  $('benchRows').prepend(tr);
}

worker.addEventListener('message', (event) => {
  const message = event.data || {};
  const waiter = pending.get(message.id);

  if (message.type === 'probe') {
    $('gpuBadge').textContent = `WebGPU: ${message.webgpu ? 'có sẵn' : 'không có'}`;
    $('gpuBadge').className = message.webgpu ? 'ok' : 'warn';
    $('isoBadge').textContent = `Cách ly: ${message.crossOriginIsolated ? 'bật' : 'tắt'}`;
    $('isoBadge').className = message.crossOriginIsolated ? 'ok' : 'warn';
    if (!runtimeTouched) {
      $('device').value = defaultRuntime.device;
      $('dtype').value = defaultRuntime.dtype;
    }
    if (waiter) {
      pending.delete(message.id);
      waiter.resolve(message);
    }
  }

  if (message.type === 'progress' && message.event) {
    const evt = message.event;
    if (typeof evt.progress === 'number') {
      $('progress').value = Math.max(0, Math.min(1, evt.progress / 100));
    }
    if (evt.file) {
      setState(`Đang tải mô hình: ${evt.file}`);
    }
  }

  if (message.type === 'status') {
    // Tin nhắn worker bằng tiếng Anh — chuyển sang trạng thái tiếng Việt khi nhận diện được.
    const raw = message.message || '';
    if (raw.startsWith('Loading model runtime')) {
      setState(modelEverLoaded ? 'Đang nạp mô hình…' : 'Đang tải mô hình lần đầu…');
    } else if (raw.startsWith('Translated')) {
      setState(`Đang dịch ${message.done} / ${message.total} đoạn`);
    } else if (raw) {
      setState(raw);
    }
    if (message.total) $('progress').value = message.done / message.total;
    log(raw);
  }

  if (message.type === 'loaded') {
    modelEverLoaded = true;
    setState(message.cached ? 'Đã có mô hình trong bộ nhớ' : 'Đã nạp mô hình');
    $('loadMetric').textContent = fmtMs(message.loadMs);
    $('progress').value = 1;
    const runtime = message.runtime ? ` (${message.runtime.device}/${message.runtime.dtype})` : '';
    log(`Đã nạp mô hình${message.cached ? ' (cache)' : ''}${runtime} trong ${fmtMs(message.loadMs)}`);
    refreshStoragePersistence({ request: true }).catch((error) => log(`Storage persist lỗi: ${error.message}`));
    if (waiter?.action === 'load') {
      pending.delete(message.id);
      waiter.resolve(message);
    }
  }

  if (message.type === 'result') {
    setBusy(false);
    setState('Xong');
    $('output').textContent = message.output;
    updateMetrics(message.metrics);
    recordBench(message.metrics);
    appendBench(message.metrics);
    $('progress').value = 1;
    log(`Xong: ${message.metrics.chunks} đoạn, ${fmtRate(message.metrics.charsPerSec)}`);
    if (waiter) {
      pending.delete(message.id);
      waiter.resolve(message);
    }
  }

  if (message.type === 'cancelled') {
    setBusy(false);
    setState('Đã huỷ');
    $('progress').value = 0;
    log('Đã huỷ');
    if (waiter) {
      pending.delete(message.id);
      waiter.resolve(message);
    }
  }

  if (message.type === 'error') {
    setBusy(false);
    setState('Lỗi');
    $('progress').value = 0;
    const friendly = describeError(message.message);
    $('output').textContent = friendly;
    log(`${message.message}\n${message.stack || ''}`);
    if (waiter) {
      pending.delete(message.id);
      waiter.reject(new Error(message.message));
    }
  }
});

function describeError(raw) {
  const text = String(raw || '');
  if (/fetch|network|load|timeout|Failed to|CORS|xet|cdn/i.test(text)) {
    return [
      '⚠ Không tải được mô hình.',
      '',
      'Thường do mạng chặn/không truy cập được CDN của Hugging Face',
      '(một số nhà mạng ở Việt Nam gặp lỗi với máy chủ Xet của HF).',
      'Hãy kiểm tra kết nối, thử lại, hoặc bật VPN/Warp rồi bấm “Dịch” lại.',
      '',
      `Chi tiết kỹ thuật: ${text}`,
    ].join('\n');
  }
  return `⚠ Có lỗi xảy ra: ${text}\n\nHãy thử lại, hoặc mở phần “Nâng cao” để đổi thiết bị/độ chính xác.`;
}

async function translate() {
  if (busy) return;
  const text = $('sourceText').value.trim();
  if (!text) {
    setState('Hãy nhập văn bản tiếng Trung trước.');
    $('sourceText').focus();
    return;
  }
  setBusy(true);
  setState(modelEverLoaded ? 'Đang dịch…' : 'Đang chuẩn bị…');
  $('progress').value = 0;
  if (!modelEverLoaded) $('firstLoadHint').hidden = false;
  try {
    await callWorker('translate', {
      config: config(),
      text: $('sourceText').value,
      options: options(),
    });
  } catch {
    // Lỗi đã được xử lý trong message handler (hiện thông báo + mở lại nút).
  } finally {
    if (modelEverLoaded) $('firstLoadHint').hidden = true;
  }
}

async function load() {
  if (busy) return;
  setBusy(true);
  setState('Đang tải mô hình…');
  $('progress').value = 0;
  if (!modelEverLoaded) $('firstLoadHint').hidden = false;
  try {
    await callWorker('load', { config: config() });
  } catch {
    // đã xử lý
  } finally {
    setBusy(false);
    $('firstLoadHint').hidden = true;
  }
}

async function runMatrix() {
  if (busy) return;
  const webgpu = $('gpuBadge').textContent.includes('có sẵn');
  const variants = [
    { device: 'wasm', dtype: 'q8' },
    ...(webgpu ? [
      { device: 'webgpu', dtype: 'fp16' },
      { device: 'webgpu', dtype: 'q8' },
    ] : []),
  ];
  for (const variant of variants) {
    $('device').value = variant.device;
    $('dtype').value = variant.dtype;
    runtimeTouched = true;
    try {
      await translate();
    } catch (error) {
      log(`Dừng đo ma trận tại ${variant.device}/${variant.dtype}: ${error.message}`);
      break;
    }
  }
}

$('translateBtn').addEventListener('click', translate);
$('copyBtn').addEventListener('click', async () => {
  const text = $('output').textContent || '';
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    setState('Đã sao chép kết quả');
  } catch {
    setState('Trình duyệt không cho phép sao chép tự động');
  }
});
$('clearBtn').addEventListener('click', () => {
  $('sourceText').value = '';
  $('output').textContent = '';
  selectedSampleKey = 'manual';
  setState('Sẵn sàng');
  $('progress').value = 0;
  $('sourceText').focus();
});

$('loadBtn').addEventListener('click', load);
$('benchBtn').addEventListener('click', translate);
$('matrixBtn').addEventListener('click', runMatrix);
$('cancelBtn').addEventListener('click', () => callWorker('cancel'));
$('persistBtn').addEventListener('click', () => refreshStoragePersistence({ request: true, userInitiated: true }));
$('copyMetricsBtn').addEventListener('click', copyMetricsReport);
$('clearMetricsBtn').addEventListener('click', () => {
  benchHistory.length = 0;
  $('benchRows').textContent = '';
  $('log').textContent = '';
});

['device', 'dtype'].forEach((id) => {
  $(id).addEventListener('change', () => {
    runtimeTouched = true;
  });
});

document.querySelectorAll('[data-sample]').forEach((button) => {
  button.addEventListener('click', () => {
    const key = button.dataset.sample;
    const samples = {
      short: sampleShort,
      dialogue: sampleDialogue,
      long: sampleLong,
      stress2k: sampleStress2k,
      stress3k: sampleStress3k,
    };
    selectedSampleKey = key;
    $('sourceText').value = samples[key] || sampleDialogue;
  });
});

// Ctrl/Cmd + Enter trong ô nhập để dịch nhanh.
$('sourceText').addEventListener('keydown', (event) => {
  if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
    event.preventDefault();
    translate();
  }
});
$('sourceText').addEventListener('input', () => {
  selectedSampleKey = 'manual';
});

callWorker('probe').catch((error) => log(`Probe lỗi: ${error.message}`));
refreshStoragePersistence({ request: false }).catch((error) => log(`Storage persist lỗi: ${error.message}`));

if (MOBILE_SAFE_MODE) {
  setState('Sẵn sàng · iOS safe mode');
  log('iOS safe mode: wasm/q8, batch<=4, chunk=160, max_new_tokens=160.');
} else if (MOBILE_DEVICE) {
  log('Android/mobile speed defaults: wasm/q8, batch=6, chunk=160, max_new_tokens=160, long batch packing on.');
} else {
  log('Desktop speed defaults: wasm/q8, batch=6, chunk=160, max_new_tokens=160, long batch packing on.');
}

function isLikelyIOSWebKit() {
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const iOSDevice = /iP(hone|ad|od)/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return iOSDevice && /AppleWebKit/i.test(ua);
}

function buildStressSample(targetChars) {
  const lines = [];
  let index = 0;
  while (lines.join('\n').length < targetChars) {
    lines.push(sampleStressSentences[index % sampleStressSentences.length]);
    index += 1;
  }
  return lines.join('\n').slice(0, targetChars);
}

async function copyMetricsReport() {
  if (!benchHistory.length) {
    setState('Chưa có số liệu để copy');
    return;
  }
  const report = {
    schema: 'moxhimt30-main-ui-bench-v1',
    copiedAt: new Date().toISOString(),
    pageUrl: location.href,
    userAgent: navigator.userAgent,
    crossOriginIsolated: globalThis.crossOriginIsolated,
    mobileSafeMode: MOBILE_SAFE_MODE,
    mobileDevice: MOBILE_DEVICE,
    defaultRuntime,
    storage: storageState,
    currentConfig: config(),
    currentOptions: options(),
    currentSourceChars: $('sourceText').value.length,
    runs: benchHistory,
    log: $('log').textContent,
  };
  try {
    await navigator.clipboard.writeText(JSON.stringify(report, null, 2));
    setState(`Đã copy ${benchHistory.length} dòng số liệu`);
  } catch {
    setState('Trình duyệt không cho phép copy số liệu tự động');
  }
}

function isLikelyMobileDevice() {
  if (navigator.userAgentData?.mobile) return true;
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  return /Android|Mobile|iP(hone|ad|od)|IEMobile|Windows Phone/i.test(ua)
    || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function selected(value, expected) {
  return value === expected ? ' selected' : '';
}

function checked(value) {
  return value ? ' checked' : '';
}
